import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Zap, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/ui-blocks";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { INDUSTRIES } from "../data";

const BENEFITS = [
  { icon: Zap, title: "48-hour shortlist", desc: "Pre-vetted candidates on your desk within two business days." },
  { icon: ShieldCheck, title: "Placement guarantee", desc: "Every permanent placement is backed by a 90-day guarantee." },
  { icon: Clock, title: "No upfront cost", desc: "You only pay when we deliver the right hire. Zero risk to start." },
];

export default function Employers() {
  const [sent, setSent] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", industry: "", roleType: "", positions: "", details: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) { toast.error("Please fill in company, name and email."); return; }
    setSent(true);
    toast.success("Request received — our team will reach out within 24 hours.");
  };

  return (
    <div data-testid="employers-page">
      <PageHeader overline="For Employers" title="Tell us who you need. We'll bring you the shortlist." subtitle="Share your requirement and a dedicated recruiter will respond within 24 hours with a plan — and often, candidates." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Stagger className="space-y-4">
                {BENEFITS.map((b) => (
                  <StaggerItem key={b.title}>
                    <div className="flex gap-4 rounded-2xl border border-slate-800/80 bg-[#111827]/60 p-6">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400"><b.icon className="w-6 h-6" /></span>
                      <div><h3 className="font-semibold text-white mb-1">{b.title}</h3><p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p></div>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
              <Reveal delay={0.2} className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-6">
                <div className="font-accent text-3xl font-bold text-amber-400 mb-1">15,000+</div>
                <p className="text-sm text-slate-400">successful placements and counting. Join 500+ companies who hire smarter with Hirezen.</p>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal>
                <div className="relative rounded-3xl border border-slate-800/80 bg-[#111827]/80 p-8 sm:p-10 overflow-hidden">
                  <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-500/10 blur-[90px] pointer-events-none" />
                  <AnimatePresence mode="wait">
                    {sent ? (
                      <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative py-12 text-center" data-testid="request-success">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30 mb-5"><CheckCircle2 className="w-8 h-8 text-amber-400" /></div>
                        <h3 className="text-2xl font-bold text-white mb-2">Request received</h3>
                        <p className="text-slate-400 max-w-md mx-auto">Thanks, {form.name.split(" ")[0] || "there"}. A Hirezen recruiter will reach out to {form.email} within 24 hours.</p>
                        <button data-testid="request-another" onClick={() => { setSent(false); setForm({ company: "", name: "", email: "", phone: "", industry: "", roleType: "", positions: "", details: "" }); }} className="mt-7 px-5 py-2.5 text-sm font-semibold text-slate-200 border border-slate-700 rounded-xl hover:border-slate-500 transition-all">Submit another request</button>
                      </motion.div>
                    ) : (
                      <motion.form key="form" onSubmit={submit} className="relative space-y-5" data-testid="request-staff-form">
                        <h3 className="text-xl font-bold text-white mb-2">Request Staff</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Field label="Company *"><Input data-testid="req-company" value={form.company} onChange={set("company")} placeholder="Acme Inc." className="input-dark" /></Field>
                          <Field label="Your name *"><Input data-testid="req-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="input-dark" /></Field>
                          <Field label="Work email *"><Input data-testid="req-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@acme.com" className="input-dark" /></Field>
                          <Field label="Phone"><Input data-testid="req-phone" value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" className="input-dark" /></Field>
                          <Field label="Industry">
                            <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                              <SelectTrigger data-testid="req-industry" className="input-dark"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i.name} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </Field>
                          <Field label="Role type">
                            <Select value={form.roleType} onValueChange={(v) => setForm((f) => ({ ...f, roleType: v }))}>
                              <SelectTrigger data-testid="req-roletype" className="input-dark"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{["Permanent", "Contract", "Executive Search", "Temporary", "RPO"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          </Field>
                        </div>
                        <Field label="Number of positions"><Input data-testid="req-positions" value={form.positions} onChange={set("positions")} placeholder="e.g. 3" className="input-dark" /></Field>
                        <Field label="Requirement details"><Textarea data-testid="req-details" value={form.details} onChange={set("details")} placeholder="Tell us about the role, must-have skills, timeline…" rows={4} className="input-dark resize-none" /></Field>
                        <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-[#0B0F19] px-4 py-3">
                          <div><div className="text-sm font-medium text-white">Urgent placement</div><div className="text-xs text-slate-500">Prioritize this requirement</div></div>
                          <Switch data-testid="req-urgent" checked={urgent} onCheckedChange={setUrgent} />
                        </div>
                        <button type="submit" data-testid="request-staff-submit-btn" className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-500/45 transition-all active:scale-[0.99]">Send Request <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></button>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-slate-400 font-medium">{label}</Label>{children}</div>
);
